/**
 * HeaderBar.ts
 * Compact, low-profile desktop top header bar (42px height).
 * Features:
 * - Brand logo & title on the left
 * - Centralized SearchBarWidget
 * - Theme / Color Scheme switcher dropdown (7 themes)
 * - Solar & Lunar Tools toggle (when sun/moon phase is active)
 * - "About" button (moved from drawer)
 * - User Profile & Auth button
 */

import store, { AppState } from '../state';
import themeService, { ThemeId, THEMES_LIST } from '../services/ThemeService';

export interface HeaderBarCallbacks {
  onAboutClick: () => void;
  onAuthClick: () => void;
  onSolarLunarClick?: () => void;
  onEnsurePoi?: () => Promise<void>;
}

export class HeaderBar {
  private container: HTMLElement;
  private logoContainer: HTMLElement;
  private searchContainer: HTMLElement;
  private actionsContainer: HTMLElement;

  private solarLunarWrapper!: HTMLElement;
  private solarLunarBtn!: HTMLButtonElement;
  private solarLunarDropdown!: HTMLElement;
  private isSolarLunarDropdownOpen: boolean = false;

  private themeBtn!: HTMLButtonElement;
  private themeDropdown!: HTMLElement;
  private aboutBtn!: HTMLButtonElement;
  private authBtn!: HTMLButtonElement;

  private isThemeDropdownOpen: boolean = false;

  constructor(
    private parentElement: HTMLElement,
    private callbacks: HeaderBarCallbacks
  ) {
    this.container = document.createElement('header');
    this.container.id = 'gw-desktop-header';
    this.container.className = 'gw-desktop-header';

    // 1. Logo Left
    this.logoContainer = document.createElement('div');
    this.logoContainer.className = 'gw-header-left';
    this.renderLogo();

    // 2. Search Center
    this.searchContainer = document.createElement('div');
    this.searchContainer.id = 'gw-header-search-container';
    this.searchContainer.className = 'gw-header-search-container';

    // 3. Actions Right
    this.actionsContainer = document.createElement('div');
    this.actionsContainer.className = 'gw-header-right';
    this.renderActions();

    this.container.appendChild(this.logoContainer);
    this.container.appendChild(this.searchContainer);
    this.container.appendChild(this.actionsContainer);

    this.parentElement.appendChild(this.container);

    this.setupSubscriptions();
    this.setupGlobalClickDismiss();
  }

  public getSearchMountElement(): HTMLElement {
    return this.searchContainer;
  }

  private renderLogo(): void {
    this.logoContainer.innerHTML = `
      <div class="gw-header-brand" title="SkySkout Weather & Outdoor Intelligence">
        <svg class="gw-header-logo-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="url(#gw-logo-grad)" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
          <path d="M7 19C10 13 14 13 17 17C20 21 24 18 25 15" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 23C13 19 16 19 19 22C21 24 23 23 24 21" stroke="rgba(255,255,255,0.6)" stroke-width="1.6" stroke-linecap="round"/>
          <circle cx="21" cy="11" r="2.5" fill="#f59e0b"/>
          <defs>
            <linearGradient id="gw-logo-grad" x1="2" y1="2" x2="30" y2="30" gradientUnits="userSpaceOnUse">
              <stop stop-color="#0284c7"/>
              <stop offset="1" stop-color="#0369a1"/>
            </linearGradient>
          </defs>
        </svg>
        <span class="gw-header-logo-text">SkySkout</span>
      </div>
    `;
  }

  private renderActions(): void {
    // 1. Solar & Lunar Tools Button & Dropdown
    this.solarLunarWrapper = document.createElement('div');
    this.solarLunarWrapper.className = 'gw-header-solar-lunar-wrapper';

    this.solarLunarBtn = document.createElement('button');
    this.solarLunarBtn.id = 'gw-header-solar-lunar-btn';
    this.solarLunarBtn.className = 'gw-header-action-btn gw-header-solar-lunar-btn';
    this.solarLunarBtn.type = 'button';
    this.solarLunarBtn.title = 'Solar & Lunar Tools, Angles & 3D AR Views';
    this.solarLunarBtn.innerHTML = `
      <span class="gw-btn-icon">🌗</span>
      <span class="gw-btn-label">Solar &amp; Lunar</span>
      <span class="gw-dropdown-arrow">▾</span>
    `;

    this.solarLunarDropdown = document.createElement('div');
    this.solarLunarDropdown.className = 'gw-header-solar-lunar-dropdown gw-hidden';
    this.renderSolarLunarDropdownOptions();

    this.solarLunarBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleSolarLunarDropdown();
    };

    this.solarLunarWrapper.appendChild(this.solarLunarBtn);
    this.solarLunarWrapper.appendChild(this.solarLunarDropdown);

    // 2. Theme / Color Scheme Switcher Button & Dropdown
    const themeWrap = document.createElement('div');
    themeWrap.className = 'gw-header-theme-wrapper';

    this.themeBtn = document.createElement('button');
    this.themeBtn.id = 'gw-header-theme-btn';
    this.themeBtn.className = 'gw-header-action-btn gw-header-theme-btn';
    this.themeBtn.type = 'button';
    this.themeBtn.title = 'Switch Color Scheme & Theme';
    this.updateThemeButtonLabel();

    this.themeDropdown = document.createElement('div');
    this.themeDropdown.className = 'gw-header-theme-dropdown gw-hidden';
    this.renderThemeDropdownOptions();

    this.themeBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleThemeDropdown();
    };

    themeWrap.appendChild(this.themeBtn);
    themeWrap.appendChild(this.themeDropdown);

    // 3. About Button
    this.aboutBtn = document.createElement('button');
    this.aboutBtn.id = 'gw-header-about-btn';
    this.aboutBtn.className = 'gw-header-action-btn gw-header-about-btn';
    this.aboutBtn.type = 'button';
    this.aboutBtn.title = 'About SkySkout, Specialized Forecast Layers & Release Notes';
    this.aboutBtn.innerHTML = `
      <span class="gw-btn-icon">❓</span>
      <span class="gw-btn-label">About</span>
    `;
    this.aboutBtn.onclick = () => {
      this.callbacks.onAboutClick();
    };

    // 4. User Profile / Auth Button
    this.authBtn = document.createElement('button');
    this.authBtn.id = 'gw-header-auth-btn';
    this.authBtn.className = 'gw-header-action-btn gw-header-auth-btn';
    this.authBtn.type = 'button';
    this.updateAuthButton(store.getState());

    this.authBtn.onclick = () => {
      this.callbacks.onAuthClick();
    };

    this.actionsContainer.appendChild(this.solarLunarWrapper);
    this.actionsContainer.appendChild(themeWrap);
    this.actionsContainer.appendChild(this.aboutBtn);
    this.actionsContainer.appendChild(this.authBtn);
  }

  private renderSolarLunarDropdownOptions(): void {
    const state = store.getState();
    this.solarLunarDropdown.innerHTML = `
      <div class="gw-solar-lunar-dropdown-header">
        <span>SOLAR &amp; LUNAR TOOLS</span>
      </div>
      <div class="gw-solar-lunar-options-list">
        <!-- 1. Sun Angles -->
        <label class="gw-solar-lunar-option-row" for="gw-toggle-sun-angles">
          <div class="gw-solar-lunar-option-left">
            <span class="gw-option-icon">☀️</span>
            <div class="gw-option-text">
              <span class="gw-option-title">Sun Angles</span>
              <span class="gw-option-desc">Sunrise, sunset &amp; sun azimuth vectors</span>
            </div>
          </div>
          <input type="checkbox" id="gw-toggle-sun-angles" class="gw-toggle-checkbox" ${state.showSunAngles ? 'checked' : ''} />
        </label>

        <!-- 2. Moon Angles -->
        <label class="gw-solar-lunar-option-row" for="gw-toggle-moon-angles">
          <div class="gw-solar-lunar-option-left">
            <span class="gw-option-icon">🌙</span>
            <div class="gw-option-text">
              <span class="gw-option-title">Moon Angles</span>
              <span class="gw-option-desc">Moonrise, moonset &amp; moon azimuth vectors</span>
            </div>
          </div>
          <input type="checkbox" id="gw-toggle-moon-angles" class="gw-toggle-checkbox" ${state.showMoonAngles ? 'checked' : ''} />
        </label>

        <div class="gw-dropdown-divider"></div>

        <!-- 3. Sun & Moon 3D AR View -->
        <label class="gw-solar-lunar-option-row" for="gw-toggle-sun-moon-3d">
          <div class="gw-solar-lunar-option-left">
            <span class="gw-option-icon">🌄</span>
            <div class="gw-option-text">
              <span class="gw-option-title">Sun &amp; Moon 3D AR View</span>
              <span class="gw-option-desc">First-person 3D terrain &amp; solar lighting</span>
            </div>
          </div>
          <input type="checkbox" id="gw-toggle-sun-moon-3d" class="gw-toggle-checkbox" ${state.sunMoonArEnabled ? 'checked' : ''} />
        </label>

        <!-- 4. Milky Way 3D AR View -->
        <label class="gw-solar-lunar-option-row" for="gw-toggle-milkyway-3d">
          <div class="gw-solar-lunar-option-left">
            <span class="gw-option-icon">🌌</span>
            <div class="gw-option-text">
              <span class="gw-option-title">Milky Way 3D AR View</span>
              <span class="gw-option-desc">Galactic Core 3D arch &amp; dark sky horizon</span>
            </div>
          </div>
          <input type="checkbox" id="gw-toggle-milkyway-3d" class="gw-toggle-checkbox" ${state.milkyWayArEnabled ? 'checked' : ''} />
        </label>

        <div class="gw-dropdown-divider"></div>

        <!-- 5. Solar & Lunar Ephemeris & Dark Skies Calendar -->
        <label class="gw-solar-lunar-option-row" for="gw-toggle-ephemeris-dock">
          <div class="gw-solar-lunar-option-left">
            <span class="gw-option-icon">📈</span>
            <div class="gw-option-text">
              <span class="gw-option-title">Solar &amp; Lunar Ephemeris Calendar</span>
              <span class="gw-option-desc">12-month calendar, diurnal curve &amp; darkness map</span>
            </div>
          </div>
          <input type="checkbox" id="gw-toggle-ephemeris-dock" class="gw-toggle-checkbox" ${state.solarLunarToolsEnabled ? 'checked' : ''} />
        </label>
      </div>
    `;

    // Bind checkbox events
    const sunAnglesCb = this.solarLunarDropdown.querySelector('#gw-toggle-sun-angles') as HTMLInputElement | null;
    if (sunAnglesCb) {
      sunAnglesCb.addEventListener('change', async (e) => {
        e.stopPropagation();
        const checked = sunAnglesCb.checked;
        if (checked && this.callbacks.onEnsurePoi) {
          await this.callbacks.onEnsurePoi();
        }
        store.update({ showSunAngles: checked });
      });
    }

    const moonAnglesCb = this.solarLunarDropdown.querySelector('#gw-toggle-moon-angles') as HTMLInputElement | null;
    if (moonAnglesCb) {
      moonAnglesCb.addEventListener('change', async (e) => {
        e.stopPropagation();
        const checked = moonAnglesCb.checked;
        if (checked && this.callbacks.onEnsurePoi) {
          await this.callbacks.onEnsurePoi();
        }
        store.update({ showMoonAngles: checked });
      });
    }

    const sunMoon3dCb = this.solarLunarDropdown.querySelector('#gw-toggle-sun-moon-3d') as HTMLInputElement | null;
    if (sunMoon3dCb) {
      sunMoon3dCb.addEventListener('change', async (e) => {
        e.stopPropagation();
        const checked = sunMoon3dCb.checked;
        this.closeSolarLunarDropdown();
        if (checked && this.callbacks.onEnsurePoi) {
          await this.callbacks.onEnsurePoi();
        }
        if (checked) {
          store.update({
            milkyWayArEnabled: false,
            sunMoonArEnabled: true
          });
        } else {
          store.update({ sunMoonArEnabled: false });
        }
      });
    }

    const milkyWay3dCb = this.solarLunarDropdown.querySelector('#gw-toggle-milkyway-3d') as HTMLInputElement | null;
    if (milkyWay3dCb) {
      milkyWay3dCb.addEventListener('change', async (e) => {
        e.stopPropagation();
        const checked = milkyWay3dCb.checked;
        this.closeSolarLunarDropdown();
        if (checked && this.callbacks.onEnsurePoi) {
          await this.callbacks.onEnsurePoi();
        }
        if (checked) {
          store.update({
            sunMoonArEnabled: false,
            milkyWayArEnabled: true
          });
        } else {
          store.update({ milkyWayArEnabled: false });
        }
      });
    }

    const ephemDockCb = this.solarLunarDropdown.querySelector('#gw-toggle-ephemeris-dock') as HTMLInputElement | null;
    if (ephemDockCb) {
      ephemDockCb.addEventListener('change', async (e) => {
        e.stopPropagation();
        const checked = ephemDockCb.checked;
        this.closeSolarLunarDropdown();
        if (checked && this.callbacks.onEnsurePoi) {
          await this.callbacks.onEnsurePoi();
        }
        if (this.callbacks.onSolarLunarClick) {
          this.callbacks.onSolarLunarClick();
        }
      });
    }
  }

  private toggleSolarLunarDropdown(): void {
    this.isSolarLunarDropdownOpen = !this.isSolarLunarDropdownOpen;
    if (this.isSolarLunarDropdownOpen) {
      this.closeThemeDropdown();
      this.renderSolarLunarDropdownOptions();
      this.solarLunarDropdown.classList.remove('gw-hidden');
    } else {
      this.solarLunarDropdown.classList.add('gw-hidden');
    }
  }

  private closeSolarLunarDropdown(): void {
    this.isSolarLunarDropdownOpen = false;
    this.solarLunarDropdown.classList.add('gw-hidden');
  }

  private updateThemeButtonLabel(): void {
    const current = themeService.getThemeMeta();
    this.themeBtn.innerHTML = `
      <span class="gw-theme-swatch" style="background: ${current.accentColor}; box-shadow: 0 0 6px ${current.accentColor}80;"></span>
      <span class="gw-btn-icon">${current.icon}</span>
      <span class="gw-btn-label">${current.name}</span>
      <span class="gw-dropdown-arrow">▾</span>
    `;
  }

  private renderThemeDropdownOptions(): void {
    const currentThemeId = themeService.getTheme();
    this.themeDropdown.innerHTML = `
      <div class="gw-theme-dropdown-header">
        <span>UI COLOR SCHEME</span>
      </div>
      <div class="gw-theme-options-list">
        ${THEMES_LIST.map(t => {
          const isSelected = t.id === currentThemeId;
          return `
            <button type="button" class="gw-theme-option-row ${isSelected ? 'active' : ''}" data-theme-id="${t.id}">
              <div class="gw-theme-option-left">
                <span class="gw-theme-preview-pill" style="background: ${t.bgPreview}; border: 1px solid ${t.accentColor};">
                  <span class="gw-theme-preview-dot" style="background: ${t.accentColor};"></span>
                </span>
                <span class="gw-theme-option-name">${t.name}</span>
              </div>
              ${isSelected ? '<span class="gw-theme-check">✓</span>' : ''}
            </button>
          `;
        }).join('')}
      </div>
    `;

    // Bind option rows
    const rows = this.themeDropdown.querySelectorAll('.gw-theme-option-row');
    rows.forEach(r => {
      r.addEventListener('click', (e) => {
        e.stopPropagation();
        const themeId = (r as HTMLElement).dataset.themeId as ThemeId;
        if (themeId) {
          themeService.setTheme(themeId);
          this.closeThemeDropdown();
        }
      });
    });
  }

  private toggleThemeDropdown(): void {
    this.isThemeDropdownOpen = !this.isThemeDropdownOpen;
    if (this.isThemeDropdownOpen) {
      this.closeSolarLunarDropdown();
      this.renderThemeDropdownOptions();
      this.themeDropdown.classList.remove('gw-hidden');
    } else {
      this.themeDropdown.classList.add('gw-hidden');
    }
  }

  private closeThemeDropdown(): void {
    this.isThemeDropdownOpen = false;
    this.themeDropdown.classList.add('gw-hidden');
  }

  private setupGlobalClickDismiss(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (this.isThemeDropdownOpen && !target.closest('.gw-header-theme-wrapper')) {
        this.closeThemeDropdown();
      }
      if (this.isSolarLunarDropdownOpen && !target.closest('.gw-header-solar-lunar-wrapper')) {
        this.closeSolarLunarDropdown();
      }
    });
  }

  private updateAuthButton(state: AppState): void {
    if (state.isAuthenticated && state.user) {
      const name = state.user.displayName || state.user.email?.split('@')[0] || 'User';
      const initials = name.substring(0, 2).toUpperCase();
      this.authBtn.innerHTML = `
        <div class="gw-header-auth-avatar-pill">${initials}</div>
        <span class="gw-btn-label">Profile</span>
        <span class="gw-auth-status-dot"></span>
      `;
      this.authBtn.title = `Signed in as ${state.user.email} (Click for Profile & Settings)`;
    } else {
      this.authBtn.innerHTML = `
        <span class="gw-btn-icon">👤</span>
        <span class="gw-btn-label">Sign In / Trial</span>
      `;
      this.authBtn.title = 'Sign In or Start Free Trial';
    }
  }

  private setupSubscriptions(): void {
    // 1. Theme updates
    themeService.subscribe(() => {
      this.updateThemeButtonLabel();
      this.renderThemeDropdownOptions();
    });

    // 2. State updates (Auth & SolarLunar tools visibility)
    store.subscribe((state) => {
      // Sync Solar Lunar Button
      if (state.timelineSunMoonPhaseEnabled !== false) {
        this.solarLunarWrapper.style.display = 'inline-flex';
      } else {
        this.solarLunarWrapper.style.display = 'none';
      }

      const isAnySolarLunarActive = !!(
        state.solarLunarToolsEnabled ||
        state.showSunAngles ||
        state.showMoonAngles ||
        state.sunMoonArEnabled ||
        state.milkyWayArEnabled
      );

      if (isAnySolarLunarActive) {
        this.solarLunarBtn.classList.add('active');
      } else {
        this.solarLunarBtn.classList.remove('active');
      }

      // If dropdown is open, sync checked states
      if (this.isSolarLunarDropdownOpen) {
        const sunCb = this.solarLunarDropdown.querySelector('#gw-toggle-sun-angles') as HTMLInputElement | null;
        if (sunCb) sunCb.checked = !!state.showSunAngles;

        const moonCb = this.solarLunarDropdown.querySelector('#gw-toggle-moon-angles') as HTMLInputElement | null;
        if (moonCb) moonCb.checked = !!state.showMoonAngles;

        const sunMoonCb = this.solarLunarDropdown.querySelector('#gw-toggle-sun-moon-3d') as HTMLInputElement | null;
        if (sunMoonCb) sunMoonCb.checked = !!state.sunMoonArEnabled;

        const mwCb = this.solarLunarDropdown.querySelector('#gw-toggle-milkyway-3d') as HTMLInputElement | null;
        if (mwCb) mwCb.checked = !!state.milkyWayArEnabled;

        const ephemCb = this.solarLunarDropdown.querySelector('#gw-toggle-ephemeris-dock') as HTMLInputElement | null;
        if (ephemCb) ephemCb.checked = !!state.solarLunarToolsEnabled;
      }

      // Sync Profile Button
      this.updateAuthButton(state);
    });
  }
}

export default HeaderBar;

